import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';

export interface OfferPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  title: string;
  /** Short facts under the title: position, ids, type. */
  meta?: React.ReactNode;
  children?: React.ReactNode;
  /** Where the offer is really configured. Opens in a new tab so the stack keeps its unsaved state. */
  openLink?: { to: string; label: string };
  onRemove?: () => void;
  removeLabel?: string;
}

/**
 * An offer's details, pinned to its row in the stack: opens beside the row (to its right, where the
 * configuration column is) and points back at it, so the order stays in view while you edit.
 */
export default function OfferPopover({
  anchorEl,
  onClose,
  title,
  meta,
  children,
  openLink,
  onRemove,
  removeLabel = 'Remove',
}: OfferPopoverProps) {
  const titleId = React.useId();
  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      marginThreshold={12}
      slotProps={{
        paper: {
          role: 'dialog',
          'aria-labelledby': titleId,
          sx: {
            width: 460,
            maxWidth: 'calc(100vw - 24px)',
            ml: 1.5,
            overflow: 'visible',
            border: 1,
            borderColor: 'divider',
          },
        },
      }}
    >
      <Box sx={{ maxHeight: 'calc(100vh - 48px)', display: 'flex', flexDirection: 'column' }}>
        <Stack direction='row' spacing={1} sx={{ alignItems: 'flex-start', px: 2, pt: 1.5, pb: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              id={titleId}
              variant='subtitle1'
              component='h3'
              sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {title}
            </Typography>
            {meta && (
              <Stack
                direction='row'
                useFlexGap
                spacing={1.5}
                sx={{ flexWrap: 'wrap', color: 'text.secondary', mt: 0.25, alignItems: 'center' }}
              >
                {meta}
              </Stack>
            )}
          </Box>
          <IconButton size='small' aria-label='Close' onClick={onClose}>
            <Close fontSize='small' />
          </IconButton>
        </Stack>
        <Stack spacing={1.5} sx={{ px: 2, pb: 2, overflowY: 'auto' }}>
          {children}
        </Stack>
        {(openLink || onRemove) && (
          <Stack
            direction='row'
            spacing={1}
            sx={{ alignItems: 'center', px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}
          >
            {onRemove && (
              <Button color='error' size='small' onClick={onRemove}>
                {removeLabel}
              </Button>
            )}
            <Box sx={{ flex: 1 }} />
            {openLink && (
              <Button
                component={RouterLink}
                to={openLink.to}
                target='_blank'
                rel='noopener noreferrer'
                data-external-link='true'
                size='small'
                variant='outlined'
                startIcon={<Search fontSize='small' />}
              >
                {openLink.label}
              </Button>
            )}
            <Button size='small' variant='contained' onClick={onClose}>
              Done
            </Button>
          </Stack>
        )}
      </Box>
    </Popover>
  );
}

/** A read-only fact in the popover's two-column grid. */
export function PopoverFact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.25,
        py: 0.75,
        bgcolor: 'background.default',
        minWidth: 0,
      }}
    >
      <Typography variant='caption' color='text.secondary' component='div'>
        {label}
      </Typography>
      <Typography variant='body2' noWrap>
        {children}
      </Typography>
    </Box>
  );
}
